import client from './client';

/** Thin wrappers so pages never hand-write URLs. */

export const auth = {
  login: (body) => client.post('/auth/admin/login', body),
  me: () => client.get('/auth/admin/me'),
  changePassword: (body) => client.put('/auth/admin/change-password', body),
};

export const reports = {
  dashboard: () => client.get('/reports/dashboard'),
  sales: (params) => client.get('/reports/sales', { params }),
  services: (params) => client.get('/reports/services', { params }),
  customers: (params) => client.get('/reports/customers', { params }),
  delivery: (params) => client.get('/reports/delivery', { params }),
  stores: (params) => client.get('/reports/stores', { params }),
  payments: (params) => client.get('/reports/payments', { params }),
};

export const orders = {
  list: (params) => client.get('/orders', { params }),
  detail: (id) => client.get(`/orders/${id}`),
  walkIn: (body) => client.post('/orders/walk-in', body),
  setStatus: (id, body) => client.patch(`/orders/${id}/status`, body),
  assign: (id, body) => client.patch(`/orders/${id}/assign`, body),
  updateItems: (id, body) => client.patch(`/orders/${id}/items`, body),
  // Reveals the price to the customer and opens their pay-online button.
  confirmBill: (id) => client.patch(`/orders/${id}/bill`),
  tags: (id) => client.post(`/orders/${id}/tags`),
  invoice: (id) => client.get(`/orders/${id}/invoice`),
};

/** Urban Wallet — points are 1:1 with rupees. */
export const wallet = {
  ledger: (userId, params) => client.get(`/wallet/admin/${userId}`, { params }),
  adjust: (body) => client.post('/wallet/admin/adjust', body),
};

export const customers = {
  list: (params) => client.get('/users', { params }),
  detail: (id) => client.get(`/users/${id}`),
  create: (body) => client.post('/users', body),
  update: (id, body) => client.put(`/users/${id}`, body),
  toggleBlock: (id) => client.patch(`/users/${id}/block`),
};

export const staff = {
  list: (params) => client.get('/staff', { params }),
  detail: (id) => client.get(`/staff/${id}`),
  create: (body) => client.post('/staff', body),
  update: (id, body) => client.put(`/staff/${id}`, body),
  toggle: (id) => client.patch(`/staff/${id}/toggle`),
  remove: (id) => client.delete(`/staff/${id}`),
};

export const roles = {
  catalogue: () => client.get('/roles/permissions'),
  list: () => client.get('/roles'),
  detail: (id) => client.get(`/roles/${id}`),
  create: (body) => client.post('/roles', body),
  update: (id, body) => client.put(`/roles/${id}`, body),
  remove: (id) => client.delete(`/roles/${id}`),
};

export const stores = {
  list: (params) => client.get('/stores', { params }),
  detail: (id) => client.get(`/stores/${id}`),
  create: (body) => client.post('/stores', body),
  update: (id, body) => client.put(`/stores/${id}`, body),
  toggle: (id) => client.patch(`/stores/${id}/toggle`),
  remove: (id) => client.delete(`/stores/${id}`),
};

export const services = {
  categories: (params) => client.get('/services/categories', { params }),
  createCategory: (body) => client.post('/services/categories', body),
  updateCategory: (id, body) => client.put(`/services/categories/${id}`, body),
  removeCategory: (id) => client.delete(`/services/categories/${id}`),
  items: (params) => client.get('/services/items', { params }),
  createItem: (body) => client.post('/services/items', body),
  updateItem: (id, body) => client.put(`/services/items/${id}`, body),
  removeItem: (id) => client.delete(`/services/items/${id}`),
  storePrice: (id, body) => client.patch(`/services/items/${id}/pricing`, body),
  bulkPrice: (body) => client.patch('/services/items/bulk-price', body),
};

export const packages = {
  list: () => client.get('/packages/admin/list'),
  preview: (body) => client.post('/packages/preview', body),
  create: (body) => client.post('/packages', body),
  update: (id, body) => client.put(`/packages/${id}`, body),
  toggle: (id) => client.patch(`/packages/${id}/toggle`),
  remove: (id) => client.delete(`/packages/${id}`),
};

export const partners = {
  list: (params) => client.get('/partners', { params }),
  create: (body) => client.post('/partners', body),
  credentials: (id) => client.get(`/partners/${id}/credentials`),
  setPassword: (id, body) => client.patch(`/partners/${id}/password`, body),
  live: () => client.get('/partners/live'),
  detail: (id) => client.get(`/partners/${id}`),
  update: (id, body) => client.put(`/partners/${id}`, body),
  reviewKyc: (id, body) => client.patch(`/partners/${id}/kyc`, body),
  earnings: (id, params) => client.get(`/partners/${id}/earnings`, { params }),
};

export const coupons = {
  list: (params) => client.get('/coupons', { params }),
  create: (body) => client.post('/coupons', body),
  update: (id, body) => client.put(`/coupons/${id}`, body),
  toggle: (id) => client.patch(`/coupons/${id}/toggle`),
  remove: (id) => client.delete(`/coupons/${id}`),
};

export const banners = {
  list: () => client.get('/banners/all'),
  create: (formData) => client.post('/banners', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => client.put(`/banners/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  remove: (id) => client.delete(`/banners/${id}`),
};

export const payments = {
  list: (params) => client.get('/payments', { params }),
  outstanding: () => client.get('/payments/outstanding'),
  collect: (body) => client.post('/payments/collect', body),
};

export const notifications = {
  list: (params) => client.get('/notifications', { params }),
  broadcast: (body) => client.post('/notifications/broadcast', body),
};

export const cms = {
  list: () => client.get('/cms'),
  create: (body) => client.post('/cms', body),
  update: (id, body) => client.put(`/cms/${id}`, body),
  remove: (id) => client.delete(`/cms/${id}`),
};

export const holidays = {
  list: (params) => client.get('/holidays', { params }),
  create: (body) => client.post('/holidays', body),
  update: (id, body) => client.put(`/holidays/${id}`, body),
  remove: (id) => client.delete(`/holidays/${id}`),
  calendar: (params) => client.get('/holidays/calendar', { params }),
};

export const locations = {
  countries: () => client.get('/locations/admin/countries'),
  createCountry: (body) => client.post('/locations/admin/countries', body),
  updateCountry: (id, body) => client.put(`/locations/admin/countries/${id}`, body),
  removeCountry: (id) => client.delete(`/locations/admin/countries/${id}`),

  cities: (params) => client.get('/locations/admin/cities', { params }),
  createCity: (body) => client.post('/locations/admin/cities', body),
  updateCity: (id, body) => client.put(`/locations/admin/cities/${id}`, body),
  removeCity: (id) => client.delete(`/locations/admin/cities/${id}`),

  areas: (params) => client.get('/locations/admin/areas', { params }),

  // Location picker
  search: (q) => client.get('/locations/search', { params: { q } }),
  resolveLink: (url) => client.post('/locations/resolve-link', { url }),
  reverseGeocode: (lat, lng) => client.get('/locations/geocode', { params: { lat, lng } }),
  createArea: (body) => client.post('/locations/admin/areas', body),
  updateArea: (id, body) => client.put(`/locations/admin/areas/${id}`, body),
  toggleArea: (id) => client.patch(`/locations/admin/areas/${id}/toggle`),
  removeArea: (id) => client.delete(`/locations/admin/areas/${id}`),
};

export const settings = {
  get: () => client.get('/settings'),
  update: (body) => client.put('/settings', body),
  otpMode: (body) => client.patch('/settings/otp-mode', body),
  charges: (body) => client.put('/settings/charges', body),
  paymentGateway: (body) => client.put('/settings/payment-gateway', body),
};

export const reviews = {
  list: (params) => client.get('/reviews', { params }),
  reply: (id, body) => client.patch(`/reviews/${id}/reply`, body),
};
